import React from 'react';
import { _, am5, am5themes_Animated } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createSeries } from './methods/create-series';
import {
  assignValueToColorBin,
  getHeatColorBoundaries,
  getHeatColorBoundariesStdDev,
  interpolateColors,
} from '../../../helpers/get-color';
import { createDownloadButton } from '../shared/create-download-button';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  download: boolean;
  category: string;
  lvl1Name: string;
  lvl1Value: string;
  lvl2Name: string;
  lvl2Value: string;
  lvl2Weight: string;
  lvl2Label: string;
  numberFormat?: string;
  heatmapColors: {
    startColor: number[];
    endColor: number[];
    middleColor: number[];
  };
  withSign: boolean;
  stdDevBoundaries: boolean;
  homeText: string;
};

const Chart = ({
  id,
  data,
  fullScreen,
  download,
  category,
  lvl1Name,
  lvl1Value,
  lvl2Name,
  lvl2Value,
  lvl2Weight,
  lvl2Label,
  heatmapColors,
  withSign,
  numberFormat,
  stdDevBoundaries,
  homeText,
}: ChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
  const [formatNumber, setFormatNumber] = React.useState<(_value: number) => string>();

  // Calculate color boundaries for both levels
  const { heatMinValue: minValue, heatMaxValue: maxValue } = React.useMemo(
    () =>
      stdDevBoundaries
        ? getHeatColorBoundariesStdDev(data.map((item) => ({ value: Number(item[lvl1Value]) })))
        : getHeatColorBoundaries(data.map((item) => ({ value: Number(item[lvl1Value]) }))),
    [data, lvl1Value, stdDevBoundaries]
  );

  const groupedData = React.useMemo(() => {
    const groupedData = _.groupBy(data, category);
    return groupedData;
  }, [data, category]);

  const childBoundaries = React.useMemo(() => {
    const boundaries: Record<string, { minValue: number; maxValue: number }> = {};

    Object.entries(groupedData).forEach(([key, items]) => {
      const values = items.map((item) => Number(item[lvl2Value]));
      const { heatMinValue, heatMaxValue } = getHeatColorBoundaries(values.map((value) => ({ value })));
      boundaries[key] = { minValue: heatMinValue, maxValue: heatMaxValue };
    });

    return boundaries;
  }, [groupedData, lvl2Value]);

  const colors = React.useMemo(() => interpolateColors(heatmapColors), [heatmapColors]);
  const references = React.useMemo(() => {
    if (!formatNumber) return [];
    return [
      {
        value: formatNumber(minValue),
        color: heatmapColors.startColor,
      },
      {
        value: formatNumber(minValue / 2),
        color:
          colors[assignValueToColorBin(minValue / 2, { minValue, maxValue }, colors.length - 1)] ??
          heatmapColors.startColor,
      },
      {
        value: formatNumber(0),
        color: heatmapColors.middleColor,
      },
      {
        value: formatNumber(maxValue / 2),
        color:
          colors[assignValueToColorBin(maxValue / 2, { minValue, maxValue }, colors.length - 1)] ??
          heatmapColors.endColor,
      },
      {
        value: formatNumber(maxValue),
        color: heatmapColors.endColor,
      },
    ];
  }, [formatNumber, colors, minValue, maxValue]);

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    // Set number format
    root.numberFormatter.set('numberFormat', numberFormat);
    setFormatNumber(() => (value: number) => root.numberFormatter.format(value));

    // Create custom theme
    const myTheme = am5.Theme.new(root);

    myTheme.rule('RoundedRectangle', ['hierarchy', 'node', 'shape', 'depth1']).setAll({
      strokeWidth: 2,
      // strokeWidth: 10,
      stroke: am5.color('#1B1F37'),
      cornerRadiusBL: 4,
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBR: 4,
    });
    myTheme.rule('RoundedRectangle', ['hierarchy', 'node', 'shape', 'depth2']).setAll({
      fillOpacity: 0,
      cornerRadiusBL: 4,
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBR: 4,
      strokeWidth: 2,
      stroke: am5.color('#1B1F37'),
    });
    myTheme.rule('Label', ['node', 'depth1']).setAll({
      fontSize: 13,
      fontWeight: 'bold',
      textAlign: 'center',
    });
    myTheme.rule('Label', ['node', 'depth2']).setAll({
      fontSize: 13,
      fontWeight: 'bold',
      textAlign: 'center',
    });
    myTheme.rule('Label', ['tooltip']).setAll({
      fontSize: 11,
    });

    // Set themes
    root.setThemes([am5themes_Animated.new(root), myTheme]);

    // Create zoomable container
    const container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.p100,
        height: am5.p100,
        wheelable: false,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
        background: am5.Rectangle.new(root, {
          fillOpacity: 0, // Set fillOpacity to 0 to ensure no background
          strokeOpacity: 0, // Remove any stroke
          strokeWidth: 0, // Ensure there's no stroke width
        }),
      })
    );

    // Set data
    createSeries({
      root,
      container,
      data,
      category,
      homeText,
      lvl1Name,
      lvl1Value,
      lvl2Name,
      lvl2Value,
      lvl2Weight,
      lvl2Label,
      colors,
      withSign,
      colorBoundaries: {
        minValue: minValue,
        maxValue: maxValue,
      },
      childBoundaries,
    });

    if (download) createDownloadButton(root, category, data, undefined, 'right');

    return () => {
      root.dispose();
    };
  }, [data]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: '100%', background: '#1B1F37', padding: '4px', borderRadius: '4px' }}>
        <div id={widgetId} style={{ width: '100%', height: '100%' }} />
      </div>
      {stdDevBoundaries && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.5rem 0',
            justifyContent: 'flex-end',
            // Fixed height here so we don't do a weird chart animation when the formatter is laoded
            height: '30px',
          }}
        >
          {references.map((item, index) => (
            <div
              key={index}
              style={{
                display: 'inline-block',
                marginRight: index < references.length - 1 ? '1px' : '0',
                height: '100%',
              }}
            >
              <div
                style={{
                  padding: '0 0.5rem',
                  height: '100%',
                  backgroundColor: `rgb(${item.color[0]}, ${item.color[1]}, ${item.color[2]})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '0.7em',
                  borderRadius: '100px',
                  marginLeft: '4px',
                }}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Chart;
